import { CHART_NO_FOCUS_OUTLINE, ChartTooltipContent, DEFAULT_CHART_HEIGHT } from '@orn/shared-web/components/Chart';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import AnimationFixToolTip from '@orn/shared-web/components/Tooltip/AnimationFixToolTip';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { Bar, BarChart as RechartsBarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { useContainerWidth } from '../../../hooks/useContainerWidth';
import { formatChartPeriodLong, formatChartPeriodShort, formatMoneyShort } from '../../../utils/formatting';
import { useDateRangeDisplayText, type CalendarDateRange } from '../../DateRangePicker';
import { ChartHeader } from '../ChartHeader';
import { calculateLabelInterval, getDateRangeForPeriod, getTransactionGroupType, TransactionGroupType } from '../utils';

import { groupTransactions } from './dataProcessing';

import type { ColumnTable } from 'arquero';

export interface ITransactionsChartDataPoint {
  period: string; // YYYY-MM-DD format
  totalAmount: number;
}

export interface ITransactionsChartProps extends React.PropsWithChildren {
  className?: string;
  transactions: ColumnTable | null;
  dateRange: CalendarDateRange;
  onDateRangeChange?: (dateRange: CalendarDateRange) => void;
  value?: string | number;
  negateAmounts?: boolean;
  isLoading?: boolean;
}

const TransactionsChart = ({
  children,
  className,
  transactions,
  dateRange,
  onDateRangeChange,
  value,
  negateAmounts = false,
  isLoading = false,
}: ITransactionsChartProps) => {
  const { containerWidth, containerRef } = useContainerWidth();
  const dateRangeDisplayText = useDateRangeDisplayText(dateRange);

  // Determine how to group transactions (day, month, or year)
  const groupType = React.useMemo(() => getTransactionGroupType(dateRange), [dateRange]);

  // Process the data
  const groupedTransactions = React.useMemo(() => {
    if (!transactions || !dateRange) {
      return [];
    }

    // dateRange already contains CalendarDate objects
    const startDate = dateRange.start;
    const endDate = dateRange.end;

    return groupTransactions({
      transactions,
      startDate,
      endDate,
      groupType,
      negateAmounts,
    });
  }, [transactions, dateRange, groupType, negateAmounts]);

  // Calculate x-axis label interval based on available space
  const xAxisInterval = React.useMemo(() => {
    return calculateLabelInterval({
      numDataPoints: groupedTransactions.length,
      containerWidth,
    });
  }, [groupedTransactions.length, containerWidth]);

  const onClickBar = (data: { payload?: { period: string } }) => {
    if (!onDateRangeChange || !data.payload?.period) {
      return;
    }

    const newDateRange = getDateRangeForPeriod(data.payload.period, groupType);
    if (newDateRange) {
      onDateRangeChange(newDateRange);
    }
  };

  const isBarClickable = onDateRangeChange && groupType !== TransactionGroupType.DAY;

  return (
    <div
      ref={containerRef}
      className={cx('bg-secondary_subtle shadow-xs flex shrink-0 flex-col overflow-hidden rounded-2xl', className)}
    >
      {/* Header */}
      <ChartHeader title={negateAmounts ? 'Inflow' : 'Spending'} value={value} subtitle={dateRangeDisplayText}>
        {children}
      </ChartHeader>

      {/* Chart Content */}
      <div className="shadow-xs px-5 pb-8 pt-5">
        {isLoading ? (
          <div className="flex items-center justify-center" style={{ height: `${DEFAULT_CHART_HEIGHT}px` }}>
            <LoadingIndicator size="md" />
          </div>
        ) : groupedTransactions.length === 0 ? (
          <div className="text-tertiary flex h-full items-center justify-center">
            No transactions in selected date range
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={DEFAULT_CHART_HEIGHT} className={CHART_NO_FOCUS_OUTLINE}>
            <RechartsBarChart
              data={groupedTransactions.map((transaction) => ({
                period: transaction.period,
                totalAmount: transaction.totalAmount,
              }))}
              margin={{
                left: -10,
                right: 20,
                top: 5,
                bottom: 5,
              }}
              className="text-tertiary [&_.recharts-text]:text-xs"
            >
              <CartesianGrid
                vertical={false}
                horizontal={true}
                stroke="currentColor"
                strokeDasharray="0"
                className="text-utility-gray-100"
              />

              <XAxis
                fill="currentColor"
                dataKey="period"
                axisLine={false}
                tickLine={false}
                tickMargin={12}
                interval={xAxisInterval}
                tickFormatter={(value: string) => formatChartPeriodShort(value, groupType)}
              />

              <YAxis
                fill="currentColor"
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                width={80}
                tickFormatter={(value: number) => formatMoneyShort(value, { precision: 'whole' })}
              />

              <AnimationFixToolTip
                content={<ChartTooltipContent />}
                formatter={(value: number) => {
                  return `$${Number(value).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}`;
                }}
                labelFormatter={(value: string) => formatChartPeriodLong(value, groupType)}
                cursor={{
                  fill: 'transparent',
                }}
              />

              <Bar
                isAnimationActive={false}
                dataKey="totalAmount"
                className={cx('fill-utility-indigo-200', isBarClickable && 'cursor-pointer')}
                radius={[6, 6, 0, 0]}
                activeBar={{ className: 'fill-utility-indigo-500' }}
                onClick={onClickBar}
              />
            </RechartsBarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default TransactionsChart;
